import { useMutation } from '@apollo/client';
import { Dialog, DialogContent } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useLogEvent } from 'hooks/useLogEvent';
import useMessages from 'hooks/useMessages';
import { UPDATE_PRODUCT_SLUG } from 'graphql/secure/mutations';
import React, { FC } from 'react';
import { IDefaultDialogProps } from 'types/dialog.types';
import { IProduct } from 'FeatureModules/ProductPage/types/product.types';
import EditSlugForm from '../Forms/EditSlugForm';
import AlertDialogHeader from 'Components/AlertDialogHeader';

interface IEditSlugDialogProps extends IDefaultDialogProps {
  productId: string;
  productSlug: string;
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  padding: theme.spacing(5, 2),

  '& .MuiDialog-paperWidthSm': {
    maxWidth: 650,
  },
}));

const StyledContent = styled(DialogContent)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

const EditSlugDialog: FC<IEditSlugDialogProps> = ({ open, productId, productSlug, onClose }) => {
  const { logEvent } = useLogEvent();
  const { setErrorMessage, setSuccessMessage } = useMessages();

  const [updateProductSlug] = useMutation(UPDATE_PRODUCT_SLUG, {
    onCompleted: ({
      updateProductSlug: {
        product: { id },
      },
    }) => {
      onClose?.();
      logEvent('UPDATE_PRODUCT_SLUG', {
        productId: id,
      });
      setSuccessMessage('URL successfully updated!');
    },
    onError: () => {
      setErrorMessage('Something went wrong updating your product URL. Please try again!');
    },
  });

  const onUpdateSlug = ({ slug }: IProduct) => {
    return updateProductSlug({
      variables: {
        id: productId,
        input: {
          slug: slug.toLowerCase(),
        },
      },
    });
  };

  return (
    <StyledDialog open={!!open} onClose={onClose}>
      <AlertDialogHeader
        title="Update product URL"
        alertMessage="By changing the URL, your product will no longer be available via the old URL"
        onClose={onClose}
      />
      <StyledContent>
        <EditSlugForm onSubmit={onUpdateSlug} slug={productSlug} onCancel={onClose} />
      </StyledContent>
    </StyledDialog>
  );
};

export default EditSlugDialog;
